import React, { useEffect, useRef } from "react";
import gsap from "gsap";
import type { SectionComponentProps } from "../../registry.js";

interface HeroSplitProps {
  headline?: string;
  subheadline?: string;
  ctaLabel?: string;
  ctaTarget?: string;
  imageUrl?: string;
  imageAlt?: string;
}

/** Splits text into word spans each wrapped in an overflow-hidden container for clip reveals. */
function WordReveal({ text, className }: { text: string; className?: string }) {
  const words = text.split(" ");
  return (
    <span className={className} aria-label={text} role="heading" aria-level={1}>
      {words.map((word, i) => (
        <span key={i} className="inline-block overflow-hidden leading-[1.15] align-bottom">
          <span className="hero-word inline-block">
            {word}
            {i < words.length - 1 && " "}
          </span>
        </span>
      ))}
    </span>
  );
}

export function HeroSplit({ props }: SectionComponentProps) {
  const {
    headline = "Welcome",
    subheadline,
    ctaLabel,
    ctaTarget = "#reservations",
    imageUrl,
    imageAlt = "",
  } = props as HeroSplitProps;

  const containerRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ defaults: { ease: "power3.out" } });

      tl.fromTo(
        ".hero-word",
        { yPercent: 115, opacity: 0 },
        { yPercent: 0, opacity: 1, duration: 0.9, stagger: 0.055 }
      )
        .fromTo(
          ".hero-sub",
          { opacity: 0, y: 18 },
          { opacity: 1, y: 0, duration: 0.65 },
          "-=0.45"
        )
        .fromTo(
          ".hero-cta",
          { opacity: 0, y: 10 },
          { opacity: 1, y: 0, duration: 0.5 },
          "-=0.3"
        )
        .fromTo(
          ".hero-image-panel",
          { opacity: 0, scale: 1.05 },
          { opacity: 1, scale: 1, duration: 1.3, ease: "power2.out" },
          0.1
        );
    }, el);

    return () => ctx.revert();
  }, []);

  return (
    <section ref={containerRef} className="grid md:grid-cols-2 min-h-[70vh]">
      <div className="flex flex-col justify-center gap-6 px-8 py-16 md:px-16 bg-background">
        <h1 className="text-4xl md:text-6xl font-bold tracking-tight text-foreground">
          <WordReveal text={headline} />
        </h1>
        {subheadline && (
          <p className="hero-sub text-lg text-muted-foreground max-w-prose">
            {subheadline}
          </p>
        )}
        {ctaLabel && (
          <a
            href={ctaTarget}
            className="hero-cta inline-flex items-center justify-center rounded-[var(--radius)] bg-primary px-8 py-3 text-sm font-semibold text-primary-foreground shadow-sm hover:opacity-90 transition-opacity w-fit"
          >
            {ctaLabel}
          </a>
        )}
      </div>
      <div className="hero-image-panel relative min-h-64 md:min-h-0 bg-muted overflow-hidden">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={imageAlt}
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-accent/20" />
        )}
      </div>
    </section>
  );
}
